import React, { useMemo, useState } from 'react';
import { X, CalendarPlus } from 'lucide-react';
import toast from 'react-hot-toast';
import { InterviewPayload } from '../../types';
import { applicationsService } from '../../services/api';
import { getApiErrorMessage } from '../../services/api';
import { INTERVIEW_ROUNDS, INTERVIEW_STATUS_META } from './kanbanConstants';
import { toDateTimeLocalValue, fromDateTimeLocalValue } from '../../utils/formatDate';

interface Props {
  applicationId: number;
  interview?: { id: number; round: string; type: string | null; interviewer: string | null; scheduledAt: string; durationMinutes: number | null; meetingLink: string | null; status: string; notes: string | null } | null;
  onClose: () => void;
  onSaved: () => void;
}

const InterviewFormModal: React.FC<Props> = ({ applicationId, interview, onClose, onSaved }) => {
  const [round, setRound] = useState(interview?.round ?? '');
  const [customRound, setCustomRound] = useState('');
  const [type, setType] = useState(interview?.type ?? '');
  const [interviewer, setInterviewer] = useState(interview?.interviewer ?? '');
  const [scheduledAt, setScheduledAt] = useState(toDateTimeLocalValue(interview?.scheduledAt));
  const [duration, setDuration] = useState(interview?.durationMinutes?.toString() ?? '');
  const [meetingLink, setMeetingLink] = useState(interview?.meetingLink ?? '');
  const [status, setStatus] = useState(interview?.status ?? 'SCHEDULED');
  const [notes, setNotes] = useState(interview?.notes ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const isCustomRound = !INTERVIEW_ROUNDS.includes(round) && round !== '';

  const roundOptions = useMemo(() => {
    if (interview && !INTERVIEW_ROUNDS.includes(interview.round) && interview.round.trim() !== '') {
      return [...INTERVIEW_ROUNDS, interview.round];
    }
    return INTERVIEW_ROUNDS;
  }, [interview]);

  const handleSave = async () => {
    const finalRound = isCustomRound && customRound.trim() ? customRound.trim() : round.trim();
    if (!finalRound) {
      setError('Interview round is required.');
      return;
    }
    if (!scheduledAt) {
      setError('Date and time are required.');
      return;
    }

    const payload: InterviewPayload = {
      round: finalRound,
      type: type || null,
      interviewer: interviewer.trim() || null,
      scheduledAt: fromDateTimeLocalValue(scheduledAt),
      durationMinutes: duration ? Number(duration) : null,
      meetingLink: meetingLink.trim() || null,
      status: status as InterviewPayload['status'],
      notes: notes.trim() || null,
    };

    setIsSaving(true);
    setError('');
    try {
      if (interview) {
        await applicationsService.updateInterview(interview.id, payload);
      } else {
        await applicationsService.createInterview(applicationId, payload);
      }
      toast.success(interview ? 'Interview updated.' : 'Interview added to your calendar.');
      onSaved();
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white rounded-card shadow-elevated w-full max-w-lg max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
          <h3 className="font-display font-semibold text-foreground">
            {interview ? 'Edit interview' : 'Schedule interview'}
          </h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-foreground transition-colors" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1.5">Round (required)</label>
            <select
              value={round}
              onChange={(e) => setRound(e.target.value)}
              className="w-full h-10 px-3 border border-zinc-300 rounded-btn text-sm text-foreground bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            >
              <option value="">Select a round…</option>
              {roundOptions.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
              <option value="__custom">Custom round…</option>
            </select>
            {round === '__custom' && (
              <input
                value={customRound}
                onChange={(e) => setCustomRound(e.target.value)}
                placeholder="e.g. Take-home challenge"
                className="mt-2 w-full h-10 px-3 border border-zinc-300 rounded-btn text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1.5">Type</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="w-full h-10 px-3 border border-zinc-300 rounded-btn text-sm text-foreground bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              >
                <option value="">No type</option>
                {[
                  ['PHONE', 'Phone'],
                  ['VIDEO', 'Video'],
                  ['ONSITE', 'On-site'],
                  ['CODING', 'Coding'],
                  ['PANEL', 'Panel'],
                  ['TEXT', 'Text'],
                ].map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1.5">Date & time (required)</label>
              <input
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                className="w-full h-10 px-3 border border-zinc-300 rounded-btn text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1.5">Interviewer</label>
            <input
              value={interviewer}
              onChange={(e) => setInterviewer(e.target.value)}
              placeholder="e.g. Sarah, Engineering Manager"
              className="w-full h-10 px-3 border border-zinc-300 rounded-btn text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1.5">Duration (minutes)</label>
              <input
                type="number"
                min={0}
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                placeholder="60"
                className="w-full h-10 px-3 border border-zinc-300 rounded-btn text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>
            {interview && (
              <div>
                <label className="block text-xs font-medium text-zinc-500 mb-1.5">Status</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full h-10 px-3 border border-zinc-300 rounded-btn text-sm text-foreground bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                >
                  {(Object.keys(INTERVIEW_STATUS_META) as Array<keyof typeof INTERVIEW_STATUS_META>).map((s) => (
                    <option key={s} value={s}>{INTERVIEW_STATUS_META[s].label}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1.5">Meeting link</label>
            <input
              value={meetingLink}
              onChange={(e) => setMeetingLink(e.target.value)}
              placeholder="https://meet.google.com/…"
              className="w-full h-10 px-3 border border-zinc-300 rounded-btn text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1.5">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Preparation notes, topics covered…"
              className="w-full px-3 py-2 border border-zinc-300 rounded-btn text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none"
            />
          </div>

          {error && (
            <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-btn px-3 py-2">{error}</p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-zinc-100">
          <button
            onClick={onClose}
            className="h-10 px-4 border border-zinc-200 hover:bg-zinc-50 text-zinc-600 text-sm font-medium rounded-btn transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="h-10 px-4 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white text-sm font-medium rounded-btn transition-colors inline-flex items-center gap-2"
          >
            <CalendarPlus className="w-4 h-4" />
            {isSaving ? 'Saving…' : interview ? 'Save changes' : 'Schedule'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InterviewFormModal;